import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

export const useMaintenanceFormatters = () => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language

  const formatters = useMemo(
    () => ({
      day: new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }),
      time: new Intl.DateTimeFormat(locale, {
        hour: 'numeric',
        minute: '2-digit',
      }),
      timeWithZone: new Intl.DateTimeFormat(locale, {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      }),
      relative: new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }),
      hours: new Intl.NumberFormat(locale, {
        style: 'unit',
        unit: 'hour',
        unitDisplay: 'short',
      }),
      minutes: new Intl.NumberFormat(locale, {
        style: 'unit',
        unit: 'minute',
        unitDisplay: 'short',
      }),
    }),
    [locale]
  )

  const formatTimestamp = useCallback(
    (date: string | Date) =>
      `${formatters.day.format(new Date(date))}, ${formatters.timeWithZone.format(new Date(date))}`,
    [formatters]
  )

  const formatSchedule = useCallback(
    ({ startsAt, endsAt }: { startsAt: string; endsAt: string | null }) => {
      if (!endsAt) {
        return t('maintenance.window.untilFurtherNotice', {
          start: formatTimestamp(startsAt),
        })
      }
      const start = `${formatters.day.format(new Date(startsAt))}, ${formatters.time.format(new Date(startsAt))}`
      const end = dayjs(startsAt).isSame(endsAt, 'day')
        ? formatters.timeWithZone.format(new Date(endsAt))
        : formatTimestamp(endsAt)
      return `${start} – ${end}`
    },
    [formatters, formatTimestamp, t]
  )

  const formatRelative = useCallback(
    ({ date, now }: { date: string | Date; now: Date }) => {
      const diffMs = dayjs(date).diff(now)
      const absMs = Math.abs(diffMs)
      if (absMs < HOUR_MS) {
        return formatters.relative.format(
          Math.round(diffMs / MINUTE_MS),
          'minute'
        )
      }
      if (absMs < 2 * DAY_MS) {
        return formatters.relative.format(Math.round(diffMs / HOUR_MS), 'hour')
      }
      return formatters.relative.format(Math.round(diffMs / DAY_MS), 'day')
    },
    [formatters]
  )

  const formatDuration = useCallback(
    (durationMs: number) => {
      const totalMinutes = Math.max(1, Math.round(durationMs / MINUTE_MS))
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      return [
        hours > 0 ? formatters.hours.format(hours) : null,
        minutes > 0 ? formatters.minutes.format(minutes) : null,
      ]
        .filter(Boolean)
        .join(' ')
    },
    [formatters]
  )

  return useMemo(
    () => ({
      formatDay: (date: string | Date) => formatters.day.format(new Date(date)),
      formatSchedule,
      formatRelative,
      formatDuration,
    }),
    [formatters, formatSchedule, formatRelative, formatDuration]
  )
}

export type MaintenanceFormatters = ReturnType<typeof useMaintenanceFormatters>
